
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/**
 * Checks if a user has permission to access the assessment
 * @param userId The user's ID
 * @param orderId Optional specific order ID to check
 * @param isFromPaymentSuccess Whether this check is from payment success page
 * @returns Object with permission status and messages
 */
export const checkAssessmentAccess = async (
  userId: string, 
  orderId?: string | null,
  isFromPaymentSuccess: boolean = false
) => {
  try {
    if (!userId) {
      return { 
        canAccess: false, 
        requiresLogin: true,
        message: "Please log in to access the assessment"
      };
    }

    // Log for debugging purposes
    console.log(`Checking assessment access - userId: ${userId}, orderId: ${orderId}, isFromPaymentSuccess: ${isFromPaymentSuccess}`);

    // First, check if the user has any completed orders
    const { data: orderData, error: orderError } = await supabase
      .from('orders')
      .select('id, status, package_type')
      .eq('user_id', userId)
      .eq('status', 'completed');

    if (orderError) {
      console.error('Error checking orders:', orderError);
      return { 
        canAccess: false, 
        requiresPayment: true,
        message: "Error checking order status. Please try again."
      };
    }

    if (!orderData || orderData.length === 0) {
      // If we're coming from payment success page, allow access temporarily
      // as the order may have just been created
      if (isFromPaymentSuccess && orderId) {
        console.log('Coming from payment success page, allowing temporary access');
        return { canAccess: true, orderId };
      }
      
      return { 
        canAccess: false, 
        requiresPayment: true,
        message: "Please purchase a package to access the assessment"
      };
    }

    // If a specific order ID was provided, check responses for that order
    if (orderId) {
      // For payment success page, we'll skip the strict validation
      if (!isFromPaymentSuccess) {
        // Verify the order ID belongs to this user and is completed
        const orderExists = orderData.some(order => order.id === orderId);
        if (!orderExists) {
          return { 
            canAccess: false, 
            requiresPayment: true,
            message: "Invalid order. Please purchase a package to access the assessment."
          };
        }
      }
      
      // Check if user has already completed the assessment for this order
      const { data: responseData, error: responseError } = await supabase
        .from('user_responses')
        .select('id')
        .eq('user_id', userId)
        .eq('order_id', orderId)
        .limit(1);

      if (responseError) {
        console.error('Error checking responses:', responseError);
        return { canAccess: true, orderId };
      }

      if (responseData && responseData.length > 0) {
        // User has already submitted responses for this order
        return { 
          canAccess: false,
          hasCompletedAssessment: true,
          message: "You have already completed the assessment for this order."
        };
      }

      // User has not submitted responses for this order
      return { canAccess: true, orderId };
    }

    // If no specific order ID, find the most recent order with no responses
    const validOrderIds = orderData.map(order => order.id);
    
    const { data: responseData, error: responseError } = await supabase
      .from('user_responses')
      .select('order_id')
      .eq('user_id', userId)
      .in('order_id', validOrderIds);

    if (responseError) {
      console.error('Error checking responses:', responseError);
      // Proceed with the most recent order if we can't check responses
      return { canAccess: true, orderId: orderData[0].id };
    }

    // Create a set of order IDs that have responses
    const ordersWithResponses = new Set(responseData?.map(r => r.order_id) || []);
    
    // Find orders without responses
    const ordersWithoutResponses = orderData.filter(order => !ordersWithResponses.has(order.id));
    
    if (ordersWithoutResponses.length === 0) {
      // All orders have associated responses
      return { 
        canAccess: false,
        hasCompletedAssessment: true,
        message: "You have already completed the assessment for all your orders."
      };
    }

    // Use the most recent order without responses
    return { canAccess: true, orderId: ordersWithoutResponses[0].id };
  } catch (error) {
    console.error('Error checking assessment access:', error);
    return { 
      canAccess: false, 
      message: "An error occurred while checking access."
    };
  }
};

/**
 * Handle redirection based on assessment access
 * @param navigate React Router's navigate function
 * @param userId User ID
 * @param orderId Optional order ID
 * @param isFromPaymentSuccess Whether this is called from payment success page
 */
export const handleAssessmentAccess = async (
  navigate: (path: string, options?: any) => void, 
  userId: string | null, 
  orderId?: string | null,
  isFromPaymentSuccess: boolean = false
) => {
  try {
    if (!userId) {
      toast.error("Please log in to access the assessment");
      navigate('/login', { state: { returnUrl: '/assessment' } });
      return;
    }

    const accessCheck = await checkAssessmentAccess(userId, orderId, isFromPaymentSuccess);

    if (accessCheck.canAccess) {
      // Store the order ID in localStorage for the assessment
      if (accessCheck.orderId) {
        localStorage.setItem('current_order_id', accessCheck.orderId);
      }
      
      // Navigate to the assessment
      navigate('/assessment');
      return;
    }

    if (accessCheck.requiresLogin) {
      toast.error(accessCheck.message);
      navigate('/login', { state: { returnUrl: '/assessment' } });
      return;
    }

    if (accessCheck.requiresPayment) {
      toast.error(accessCheck.message);
      navigate('/');
      return;
    }

    if (accessCheck.hasCompletedAssessment) {
      toast.info(accessCheck.message);
      navigate('/my-orders');
      return;
    }

    // Fallback for any other condition
    toast.error(accessCheck.message || "Unable to access assessment");
    navigate('/');
  } catch (error) {
    console.error('Error handling assessment access:', error);
    toast.error("An error occurred. Please try again.");
    navigate('/');
  }
};
